import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { DisplayType, SearchMessageType, TemplateDisplayType } from '../Types'
import { FlexHorizontal, IBoard, MessageCard, Spacer } from '@vestaboard/installables'
import React, { useState } from 'react'
import { TemplateMessageSortBy, TemplateMessageSortDirection } from '../Types'
import { useHistory, useLocation } from 'react-router'

import { Alert } from '@material-ui/lab'
import { Check } from '@material-ui/icons'
import { CheckCircleIcon } from '@material-ui/x-grid'
import { GridCloseIcon } from '@material-ui/data-grid'
import InfiniteScroll from 'react-infinite-scroller'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { Link } from '../../link'
import { MessageModal } from '../../message-v2'
import { MessageTemplateTabs } from '../components/Tabs'
import { SearchInput } from '../../../ui/SearchInput'
import { TruncatedId } from '../../truncated-id'
import moment from 'moment'
import { useListTemplateCategories } from '../hooks/useListTemplateCategories'
import { useListTemplateMessages } from '../hooks/useListTemplateMessages'
import { useListTemplateSearch } from '../hooks/useListTemplateSearch'

const PER_PAGE = 20

export const ListMessages = () => {
  const history = useHistory()
  const [cardSize, setCardSize] = useState<string | null>('22x6')
  const [messageId, setMessageId] = useState<string | null>(null)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const categoryId = searchParams.get('categoryId') || null
  const terms = searchParams.get('terms') || null
  const [sortBy, setSortBy] = useState<'featured' | 'new' | 'top'>('new')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const { data: categoryData } = useListTemplateCategories({
    input: {}
  })
  const { loading: listLoading, error, data, fetchMore } = useListTemplateMessages(
    {
      input: {
        limit: PER_PAGE,
        cursor: null,
        categoryId,
        sortBy: TemplateMessageSortBy[sortBy],
        sortDirection: TemplateMessageSortDirection[sortDirection],
        displayType: cardSize === '15x3' ? TemplateDisplayType.note : TemplateDisplayType.flagship
      }
    },
    !!terms
  )
  const { loading: searchLoading, data: searchData } = useListTemplateSearch(
    {
      input: {
        term: terms || '',
        cursor: null,
        limit: PER_PAGE,
        types: [SearchMessageType.TemplatedMessage],
        displayType: cardSize === '15x3' ? DisplayType.note : DisplayType.flagship
      }
    },
    !terms
  )
  const loading = listLoading || searchLoading

  const category = categoryId
    ? (categoryData?.listTemplateCategories?.templateCategories || []).find(category => category.id === categoryId)
    : null

  return (
    <Box>
      <MessageModal messageId={messageId} onClose={() => setMessageId(null)} />
      <FlexHorizontal spaceBetween>
        <FlexHorizontal>
          <Typography variant='h4'>Template Messages</Typography>
          {category ? (
            <>
              <ButtonBase
                style={{
                  marginLeft: 12
                }}
                onClick={() => {
                  history.push(`/message-templates`)
                }}>
                <Chip
                  label={
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='body2'>{category?.title}</Typography>
                      <GridCloseIcon />
                    </Box>
                  }
                />
              </ButtonBase>
            </>
          ) : null}
        </FlexHorizontal>
        <FlexHorizontal>
          <Select
            value={cardSize}
            onChange={e => {
              const value = e.target.value as string
              setCardSize(value)
            }}
            variant='outlined'>
            <MenuItem value='22x6'>Flagship</MenuItem>
            <MenuItem value='15x3'>Note</MenuItem>
          </Select>
          <div style={{ width: 12 }} />
          <SearchInput
            size='medium'
            onSearch={term => {
              history.push(`/message-templates?terms=${term || ''}`)
            }}
            value={terms || ''}
          />
          <div style={{ width: 12 }} />
          <Button onClick={() => history.push('/message-templates/message/create')} variant='contained' color='primary'>
            Create Template Message
          </Button>
        </FlexHorizontal>
      </FlexHorizontal>
      <Box>
        <Spacer size='extraLarge' />
        <MessageTemplateTabs activeTab='messages' />
      </Box>
      <Spacer size='large' />
      {loading && (terms ? !searchData : !data) ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading the template suggestions</Alert>
      ) : (
        <InfiniteScroll
          loader={
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <Spacer size='large' />
              <CircularProgress />
            </Box>
          }
          hasMore={terms ? false : !!data?.listTemplateMessages.nextCursor && !loading}
          loadMore={async () => {
            const nextCursor = data?.listTemplateMessages.nextCursor

            if (nextCursor) {
              await fetchMore({
                variables: {
                  input: {
                    cursor: nextCursor,
                    limit: PER_PAGE,
                    categoryId,
                    sortBy: TemplateMessageSortBy[sortBy],
                    sortDirection: TemplateMessageSortDirection[sortDirection]
                  }
                },
                updateQuery: (previousQueryResult, options) => {
                  const fetchMoreResult = options.fetchMoreResult
                  const prev = previousQueryResult
                  const possiblyDuplicatedTemplateMessages = [
                    ...prev.listTemplateMessages.templateMessages,
                    ...(fetchMoreResult?.listTemplateMessages?.templateMessages || [])
                  ]

                  const seenIds = new Set()
                  const uniqueTemplateMessages = possiblyDuplicatedTemplateMessages.filter(message => {
                    if (seenIds.has(message.id)) {
                      return false
                    } else {
                      seenIds.add(message.id)
                      return true
                    }
                  })

                  return {
                    listTemplateMessages: {
                      __typename: 'ListTemplateMessagesV2',
                      templateMessages: uniqueTemplateMessages,
                      nextCursor: fetchMoreResult?.listTemplateMessages?.nextCursor || null
                    }
                  }
                }
              })
            }
          }}>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 140 }}>ID</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell
                    style={{ width: 140, cursor: 'pointer' }}
                    onClick={() => {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                      setSortBy('new')
                    }}>
                    Created
                    {sortBy === 'new' ? (
                      sortDirection === 'asc' ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )
                    ) : null}
                  </TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell
                    style={{ width: 120, cursor: 'pointer' }}
                    onClick={() => {
                      if (sortBy !== 'featured') {
                        setSortBy('featured')
                      } else {
                        setSortBy('new')
                      }
                    }}>
                    Featured
                    {sortBy === 'featured' ? <CheckCircleIcon /> : null}
                  </TableCell>
                  <TableCell
                    style={{ cursor: 'pointer', width: 100 }}
                    onClick={() => {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                      setSortBy('top')
                    }}>
                    Likes
                    {sortBy === 'top' ? (
                      sortDirection === 'asc' ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )
                    ) : null}
                  </TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell style={{ width: 250 }}>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(terms
                  ? searchData?.listSearchMessages?.searchMessages || []
                  : data?.listTemplateMessages.templateMessages || []
                ).map((colorTemplate: any) => (
                  <TableRow key={colorTemplate.id}>
                    <TableCell>
                      <TruncatedId value={colorTemplate.id} path={`/message-templates/message/${colorTemplate.id}`} />
                    </TableCell>
                    <TableCell>
                      <Link to={`/message-templates?categoryId=${colorTemplate.categoryId}`}>
                        {colorTemplate.attribution}
                      </Link>
                    </TableCell>
                    <TableCell>{moment(colorTemplate.created).format('MMM D, YYYY')}</TableCell>
                    <TableCell>
                      {colorTemplate.message.tags?.map((tag: string) => (
                        <Chip label={tag} key={tag} style={{ margin: 3, fontSize: 9 }} size='small' />
                      ))}
                    </TableCell>
                    <TableCell>{colorTemplate.isFeatured ? <Check /> : null}</TableCell>
                    <TableCell>{colorTemplate.likes?.toLocaleString()}</TableCell>
                    <TableCell>{colorTemplate.message.text}</TableCell>
                    <TableCell>
                      <Box>
                        <MessageCard
                          characters={colorTemplate.message.characters as IBoard}
                          boardStyle={cardSize === '15x3' ? 'note-black' : 'black'}
                          onEditPress={() => {
                            setMessageId(colorTemplate.message.id)
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      )}
    </Box>
  )
}
